import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { useRepository } from 'hooks/auth'
import { useDialogs } from 'hooks/dialogs'
import { useTranslationTyped } from 'i18n/i18n'
import { useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'
import { navigateOutsideIframe } from 'util/iframe'

type CreatePaymentParams = Parameters<
  StudentAssignmentCubit['createPayment']
>[0]

/**
 * Shared "Pay with Stripe" flow for the purchase dialogs. Stripe Checkout
 * refuses to render inside an iframe (Canvas, Simple Syllabus, ...), so the
 * redirect goes through `navigateOutsideIframe`. When the browser blocks the
 * new tab, `blockedCheckoutUrl` is set and the dialog should render
 * `StripeCheckoutBlockedView` so the student can open it themselves.
 */
export function useStripeCheckout(cubit: StudentAssignmentCubit) {
  const repo = useRepository()
  const { popDialog } = useDialogs()
  const { tt } = useTranslationTyped()
  const [waitingForStripeURL, setWaitingForStripeURL] = useState(false)
  const [blockedCheckoutUrl, setBlockedCheckoutUrl] = useState<string | null>(
    null
  )

  const startCheckout = useCallback(
    async (
      params: CreatePaymentParams,
      events: { clicked: string; failed?: string }
    ) => {
      repo.logEvent(events.clicked)
      setBlockedCheckoutUrl(null)
      setWaitingForStripeURL(true)
      try {
        const result = await cubit.createPayment(params)

        if (result.action === 'purchasedSectionPass') {
          popDialog()
          toast.success(
            tt.pages.student.assignment.dialogs.purchase_dialog_body.successfully_redeemed_rebates()
          )
          return
        }

        const outcome = navigateOutsideIframe(result.paymentUrl)
        if (outcome === 'blocked') {
          repo.logEvent('stripe_checkout_new_tab_blocked')
          setBlockedCheckoutUrl(result.paymentUrl)
          setWaitingForStripeURL(false)
          return
        }
        if (outcome === 'opened_new_tab') {
          repo.logEvent('stripe_checkout_opened_in_new_tab')
          toast.success(
            tt.student_assignment.pay_with_stripe_opened_in_new_tab()
          )
        } else {
          repo.logEvent('stripe_checkout_redirected')
        }
        popDialog()
      } catch (e) {
        if (events.failed) repo.logEvent(events.failed)
        toast.error(tt.student_assignment.pay_with_stripe_failure())
        popDialog()
        // rethrow to bubble to global error handler (sentry)
        throw e
      }
    },
    [cubit, popDialog, repo, tt]
  )

  return { startCheckout, waitingForStripeURL, blockedCheckoutUrl }
}
